import { Component, Input, OnDestroy } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { Router } from '@angular/router'
import {
  ApiService,
  Appointment,
  AppointmentModel,
  ConfirmService,
  FileService,
  Intake,
  intakeDisplayStatus,
  IntakeModel,
  intakeStatusColor,
  intakeStatusText,
  MessageTemplate,
  Patient,
  PatientPalRestError,
  Permission,
  ProgressIndicatorService, SendPatientMessageMethod } from '@mmx/shared'
import { get } from 'lodash'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { IntakeCodeDialogComponent } from './intake-code-dialog.component'

@Component({
  selector: 'mmx-intake-status-icon',
  templateUrl: './intake-status-icon.component.html',
  standalone: false,
})
export class IntakeStatusIconComponent implements OnDestroy {
  @Input() patient?: Patient
  @Input() appointment?: AppointmentModel
  @Input() intake?: IntakeModel
  @Input() status?: Appointment.INTAKE_STATUS
  @Input() workflow = false

  public readonly Permission = Permission
  public INTAKE_STATUS = Appointment.INTAKE_STATUS
  public inProgress = false
  public intakeStatusColor = intakeStatusColor
  public intakeDisplayStatus = intakeDisplayStatus
  public intakeStatusText = intakeStatusText

  private onDestroy$ = new Subject<void>()

  get url() {
    return this.appointment.intakeId
      ? [
        '/patient',
        this.appointment.patientId,
        'appointment',
        this.appointment.id,
        'intake',
        this.appointment.intakeId,
      ]
      : [this.appointment.url]
  }

  get canSendIntakeLinkToPatient(): boolean {
    return (
      !this.appointment.cancelled &&
      ![
        Appointment.INTAKE_STATUS.SUBMITTED,
        Appointment.INTAKE_STATUS.CONFIRMED,
        Appointment.INTAKE_STATUS.TECH_CONFIRMED,
        Appointment.INTAKE_STATUS.TECH_REVIEW,
        Appointment.INTAKE_STATUS.SYNCING,
        Appointment.INTAKE_STATUS.SYNCED,
        Appointment.INTAKE_STATUS.NOT_REQUIRED,
      ].includes(this.appointment.intakeStatus)
    )
  }

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private confirmService: ConfirmService,
    private restService: ApiService,
    private progress: ProgressIndicatorService,
    private fileService: FileService,
  ) {}

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  startIntake() {
    if (this.inProgress) {
      return
    }

    this.inProgress = true

    const intake = new IntakeModel({
      appointmentId: this.appointment.id,
      patientId: this.patient.id,
      givenName: this.patient.name.given,
      familyName: this.patient.name.family,
      email: this.patient.email,
      dob: this.patient.dob?.format('YYYY-MM-DD'),
      phoneNumber: this.patient.phone,
      address: this.patient.address,
      stage: Intake.STAGE.PENDING,
    })

    this.restService
      .createIntake(intake)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.router.navigate([
              'patient',
              this.patient.id,
              'appointment',
              this.appointment.id,
              'intake',
              response.data.id,
            ])
          }
        },
        error: (err: PatientPalRestError) => {
          const errorMessage =
            get(err, 'error.errors[0].message') ||
            err.error.message ||
            'Unable to add intake. Please try again.'
          this.progress.snack({
            message: errorMessage,
            panelClass: 'warn',
          })
        },
      })
  }

  gotoIntake() {
    this.router.navigate(this.url)
  }

  downloadMenuOpened() {
    if (this.inProgress || this.intake != null) {
      return
    }

    this.inProgress = true
    this.restService.getIntake(this.appointment.intakeId).subscribe(
      (intake) => {
        this.inProgress = false
        this.intake = intake
      },
      () => {
        this.inProgress = false
      },
    )
  }

  download(event: MouseEvent, file?: string) {
    const asHtml = event.altKey // if they hold the alt key, grab the PDF output as HTML (this is for debugging)
    this.inProgress = true
    this.restService
      .getIntakePdfLink(this.appointment.intakeId, {
        file,
        html: asHtml,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (resp) => {
          this.fileService.saveAs(resp.data)
          this.inProgress = false
        },
        () => {
          this.inProgress = false
          this.progress.snack({
            message:
              'An error occurred while trying to download the intake document',
            panelClass: 'warn',
          })
        },
      )
  }

  generateCode() {
    this.inProgress = true

    this.restService.createIntakeCode(this.appointment.id).subscribe(
      (resp) => {
        this.inProgress = false
        this.dialog.open(IntakeCodeDialogComponent, {
          data: resp.data,
          panelClass: 'dialog-form',
        })
      },
      () => {
        this.inProgress = false
        this.progress.snack({
          message: 'Failed to create an intake code, please try again',
          panelClass: 'warn',
        })
      },
    )
  }

  openSendMessageConfirmationDialog(method: SendPatientMessageMethod) {
    const isMessageAlreadySent =
      this.appointment.intakeStatus === Appointment.INTAKE_STATUS.DELIVERED
    const message =
      'This patient has already received an intake message. Would you like to send it again?'

    if (isMessageAlreadySent) {
      this.confirmService
        .confirm(message)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((isConfirmed) => {
          if (isConfirmed) {
            this.sendMessage(method)
          }
        })
    } else {
      this.sendMessage(method)
    }
  }

  private sendMessage(method: SendPatientMessageMethod) {
    this.inProgress = true
    this.restService
      .sendPatientMessage(this.patient.id, {
        type: MessageTemplate.TYPE.INTAKE_LINK,
        method,
        appointmentId: this.appointment.id,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: () => {
          if (
            this.appointment.intakeStatus === Appointment.INTAKE_STATUS.NONE
          ) {
            this.appointment.intakeStatus = Appointment.INTAKE_STATUS.DELIVERED
          }
          this.inProgress = false
          this.progress.snack('Message has been sent')
        },
        error: (err: PatientPalRestError) => {
          this.inProgress = false
          this.progress.snack({
            message: `Failed to send message. ${err.error?.message}`,
          })
        },
      })
  }
}
